import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import type { ReactNode } from 'react';

// Define the shape of the context state
interface AgentState {
  isMenuPinned: boolean;
  globalDateFormat: string;
}

// Define a generic action that automatically infers from AgentState
interface Action<K extends keyof AgentState> {
  type: K;
  value: AgentState[K];
}

// Helper function to get initial state from window.store if available
// This prevents race condition by reading the actual state on mount
function getInitialState(): AgentState {
  if (typeof window !== 'undefined') {
    try {
      const state = window.store?.getState?.();
      if (state) {
        return {
          isMenuPinned: state.user?.isMenuPinned ?? false,
          globalDateFormat:
            state.user?.headerMenu.ecmDateFormat ?? 'MMM dd, yyyy',
        };
      }
    } catch {
      // ignore — fall through to defaults if window.store is malformed
    }
  }

  return {
    isMenuPinned: false,
    globalDateFormat: 'MMM dd, yyyy',
  };
}

// Initial state - read from store if available to prevent race condition
export const initialState: AgentState = getInitialState();

// Reducer function that infers the key and value types
const reducer = <K extends keyof AgentState>(
  state: AgentState,
  action: Action<K>
): AgentState => ({ ...state, [action.type]: action.value });

// Create the context with a default value
const AgentContext = createContext<
  | {
      agentState: AgentState;
      updateAgentState: <K extends keyof AgentState>(
        key: K,
        value: AgentState[K]
      ) => void;
    }
  | undefined
>(undefined);

// Provider component that wraps the children
export function AgentProvider({ children }: { children: ReactNode }) {
  const [agentState, dispatch] = useReducer(reducer, initialState);

  // Function to dispatch actions based on key and value, with no hardcoding
  const updateAgentState = useCallback(
    <K extends keyof AgentState>(key: K, value: AgentState[K]) => {
      dispatch({ type: key, value }); // No hardcoded strings!
    },
    []
  );

  // Memoize the context value to avoid unnecessary re-renders
  const value = useMemo(
    () => ({
      agentState,
      updateAgentState,
    }),
    [agentState, updateAgentState]
  );

  return (
    <AgentContext.Provider value={value}>{children}</AgentContext.Provider>
  );
}

// Custom hook to use the Agent context
export const useAgentContext = () => {
  const context = useContext(AgentContext);

  if (!context) {
    return {
      agentState: initialState,
      updateAgentState: () => {},
    };
  }

  return context;
};

// Hook to access the Agent state (for backward compatibility)
export function useAgentState() {
  const { agentState } = useAgentContext();
  return {
    isMenuPinned: agentState.isMenuPinned,
    globalDateFormat: agentState.globalDateFormat,
  };
}
